import {
    AssessmentOutlined,
    CallMade,
    CallReceived,
    DonutLargeOutlined,
    Inventory2Outlined,
    LayersOutlined,
    LocalOffer,
    LockOutlined,
    PaymentsOutlined,
    PlayArrowRounded,
    ReceiptLongOutlined,
    Schedule,
    ShowChart,
    Wallet,
} from '@mui/icons-material';
import {
    Box,
    Button,
    CircularProgress,
    Grid,
    Table,
    TableBody,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import { alpha } from '@mui/material/styles';
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from 'react-router-dom';
import { RealInput } from "../../../core/components/RealInput";
import { useAutenticacaoContext } from '../../../core/contexts/AutenticacaoContext';
import { useThemeContext } from '../../../core/contexts/ThemeContext';
import useCores from '../../../core/hooks/useCores';
import { useDataHora } from "../../../core/hooks/useDataHora";
import { useMoeda } from "../../../core/hooks/useMoeda";
import CaixaTableRow from "../../components/CaixaTableRow";
import { ConferenciaCaixa } from "../../components/ConferenciaCaixa";
import { MarketplaceSwitches } from "../../components/MarketplaceSwitches";
import { useCaixa } from "../../hooks/useCaixa";
import { useFetchCaixaAberto } from "../../hooks/useFetchCaixaAberto";
import { IItemConta } from '../../interfaces/IItemConta';
import { CartaoIndicador } from './components/CartaoIndicador';
import { GraficoRosca } from './components/GraficoRosca';
import { TabelaResumo } from './components/TabelaResumo';
import {
    CabecalhoCartao,
    Cartao,
    CelulaCabecalho,
    EstadoVazio,
    PilulaStatus,
    Rotulo,
    TituloCartao,
} from "../../../core/styles/superficies";
import { PaginaCaixa } from "./styles";

export const Caixa = () => {
    const [conferenciaAberta, setConferenciaAberta] = useState(false);
    const { abrirCaixa, loading, fecharCaixa } = useCaixa();
    const {
        caixa,
        ultimoCaixa,
        handleRefresh,
        isLoadingContasAReceber,
        totalContasAReceber,
        isLoadingContasAPagar,
        totalContasAPagar,
        isLoading
    } = useFetchCaixaAberto();
    const { real, number } = useMoeda();
    const { horaBr, dataHoraBr } = useDataHora();
    const [descricao, setDescricao] = useState(caixa ? caixa?.descricao : `Intervalo de caixa do dia ${dataHoraBr()}`);
    const [valorInicial, setValorInicial] = useState<any>(0.00);
    const navigate = useNavigate();
    const { theme } = useThemeContext();
    const { verificarAcessoFuncionalidade, perfil } = useAutenticacaoContext();

    const handleCaixaChange = async () => {
        if (loading) {
            return;
        }

        if (caixa) {
            await fecharCaixa();
            return navigate(0)
        }

        await abrirCaixa(descricao, parseFloat(valorInicial));
        return handleRefresh();
    }

    useEffect(() => {
        if (ultimoCaixa && !valorInicial) {
            setValorInicial(ultimoCaixa.valorFinal)
            return
        }
        if (caixa) {
            setValorInicial(caixa.valorInicial)
            return
        }
    }, [caixa, ultimoCaixa])

    useEffect(() => {
        if (perfil && !verificarAcessoFuncionalidade("Gestão de caixa")) {
            navigate("/nao-autorizado")
        }
    }, [perfil])

    const itensAgrupados = useMemo(() => {
        if (caixa) {
            const itensConta: IItemConta[] = [];

            caixa.contas.forEach(conta => {
                conta.itens.forEach(item => {
                    const indexItem = itensConta.findIndex(itemConta => itemConta.descricao === item.descricao);

                    if (indexItem === -1) {
                        itensConta.push({ ...item });
                    } else {
                        itensConta[indexItem].valorTotal += item.valorTotal;
                        itensConta[indexItem].quantidade += item.quantidade;
                    }
                });
            });

            return itensConta.sort((a, b) => b.quantidade - a.quantidade);
        }

        return [];
    }, [caixa]);

    const metodosPagamentoAgrupados = useMemo(() => {
        if (caixa) {
            const pagamentosConta: IItemConta[] = [];

            caixa.contas.forEach(conta => {
                conta.pagamentos?.forEach(pagamento => {
                    const indexPagamento = pagamentosConta.findIndex(p => p.descricao === pagamento.metodoPagamento);

                    if (indexPagamento === -1) {
                        if (pagamento.metodoPagamento) {
                            pagamentosConta.push({
                                descricao: pagamento.metodoPagamento,
                                quantidade: 1,
                                valorTotal: number(pagamento.valor as any) ?? 0
                            });
                        } else {
                            const indexIndefinido = pagamentosConta.findIndex(p => p.descricao === "Indefinido");
                            if (indexIndefinido !== -1) {
                                pagamentosConta[indexIndefinido].valorTotal += number(pagamento.valor as any) ?? 0;
                                pagamentosConta[indexIndefinido].quantidade += 1;
                            } else {
                                pagamentosConta.push({
                                    descricao: "Indefinido",
                                    quantidade: 1,
                                    valorTotal: number(pagamento.valor as any) ?? 0
                                });
                            }
                        }
                    } else {
                        pagamentosConta[indexPagamento].valorTotal += number(pagamento.valor as any) ?? 0;
                        pagamentosConta[indexPagamento].quantidade += 1;
                    }
                });
            });

            return pagamentosConta.sort((a, b) => b.quantidade - a.quantidade);
        }

        return [];
    }, [caixa]);

    const subitensAgrupados = useMemo(() => {
        if (caixa) {
            const subitensConta: IItemConta[] = [];

            caixa.contas.map(conta => {
                conta.itens.map(item => {
                    item.subitens?.map(subitem => {
                        const indexSubitem = subitensConta.findIndex(subitemConta => subitemConta.descricao === subitem.descricao);

                        if (indexSubitem === -1) {
                            subitensConta.push({
                                descricao: subitem.descricao,
                                quantidade: (subitem.quantidade * item.quantidade),
                                valorTotal: ((subitem.valor * subitem.quantidade) * item.quantidade)
                            })
                        } else {
                            subitensConta[indexSubitem].quantidade += (subitem.quantidade * item.quantidade)
                            subitensConta[indexSubitem].valorTotal += ((subitem.valor * subitem.quantidade) * item.quantidade)
                        }
                    })
                })
            })

            return subitensConta.sort((a, b) => b.quantidade - a.quantidade);
        }


        return [];
    }, [caixa]);

    const cores = useCores('vibrant', 6);

    const corStatus = caixa ? theme.palette.success.main : theme.palette.error.main;
    const temContas = !!caixa?.contas?.length;

    return (
        <>
            <PaginaCaixa>
                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 2,
                        alignItems: "center",
                        justifyContent: "space-between",
                    }}
                >
                    <Box>
                        <Typography variant="h5" fontWeight={700}>Caixa</Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mt: 1, flexWrap: "wrap" }}>
                            <PilulaStatus cor={corStatus}>
                                {caixa ? "Caixa aberto" : "Caixa fechado"}
                            </PilulaStatus>
                            <Typography variant="body2" color="text.secondary">
                                Estabelecimento {caixa ? "atendendo" : "não atendendo"} <strong>pedidos</strong>
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                        <Button
                            variant="outlined"
                            startIcon={<AssessmentOutlined />}
                            disabled={!caixa}
                            onClick={() => setConferenciaAberta(true)}
                        >
                            Conferência de caixa
                        </Button>
                        <Button
                            variant="contained"
                            color={caixa ? "error" : "primary"}
                            disableElevation
                            onClick={handleCaixaChange}
                            startIcon={
                                loading
                                    ? <CircularProgress size={16} color="inherit" />
                                    : caixa ? <LockOutlined /> : <PlayArrowRounded />
                            }
                        >
                            {caixa ? "Fechar caixa" : "Abrir caixa"}
                        </Button>
                    </Box>
                </Box>

                <Cartao>
                    <Box sx={{ padding: 2.5 }}>
                        {caixa ? (
                            <>
                                <Rotulo>Intervalo</Rotulo>
                                <Typography sx={{ mt: 0.5, fontSize: "1.0625rem", fontWeight: 600 }}>
                                    {caixa.descricao || descricao}
                                </Typography>
                            </>
                        ) : (
                            <>
                                <Rotulo>Abertura do intervalo</Rotulo>
                                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mt: 1.5 }}>
                                    <TextField
                                        label="Descrição"
                                        variant="outlined"
                                        size="small"
                                        sx={{ flex: "1 1 320px" }}
                                        onChange={(e) => setDescricao(e.target.value)}
                                        value={descricao}
                                    />
                                    <TextField
                                        label="Valor inicial"
                                        size="small"
                                        sx={{ width: 200 }}
                                        value={valorInicial}
                                        onChange={(e) => setValorInicial(e.target.value)}
                                        name="numberformat"
                                        id="formatted-numberformat-input"
                                        InputProps={{
                                            inputComponent: RealInput as any,
                                        }}
                                    />
                                </Box>
                            </>
                        )}
                    </Box>
                </Cartao>

                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} lg={4}>
                        <CartaoIndicador
                            rotulo="Início do dia"
                            valor={real(valorInicial)}
                            icone={<Wallet />}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} lg={4}>
                        <CartaoIndicador
                            rotulo="Agora"
                            valor={caixa ? real(caixa.valorFinal) : "R$ 0,00"}
                            icone={<ShowChart />}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} lg={4}>
                        <CartaoIndicador
                            rotulo="Aberto às"
                            valor={caixa ? horaBr(caixa.abertoAs) : "-"}
                            icone={<Schedule />}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} lg={4}>
                        <CartaoIndicador
                            rotulo="Contas a receber"
                            valor={real(totalContasAReceber ?? 0.00)}
                            icone={<CallReceived />}
                            cor={theme.palette.success.main}
                            carregando={isLoadingContasAReceber}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} lg={4}>
                        <CartaoIndicador
                            rotulo="Contas a pagar"
                            valor={real(totalContasAPagar ?? 0.00)}
                            icone={<CallMade />}
                            cor={theme.palette.error.main}
                            carregando={isLoadingContasAPagar}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} lg={4}>
                        <CartaoIndicador
                            rotulo="Número de vendas"
                            valor={caixa?.contas.length ?? 0}
                            icone={<LocalOffer />}
                            carregando={isLoading}
                        />
                    </Grid>
                </Grid>

                <MarketplaceSwitches caixaAberto={!!caixa} />

                <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                        <TabelaResumo
                            titulo="Itens mais vendidos"
                            dados={temContas ? itensAgrupados : []}
                            mensagemVazia="Nenhum item registrado"
                            icone={<Inventory2Outlined />}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TabelaResumo
                            titulo="Subitens mais vendidos"
                            dados={temContas ? subitensAgrupados : []}
                            mensagemVazia="Nenhum subitem registrado"
                            icone={<LayersOutlined />}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TabelaResumo
                            titulo="Principais métodos de pagamento"
                            dados={temContas ? metodosPagamentoAgrupados : []}
                            mensagemVazia="Nenhum método de pagamento registrado"
                            icone={<PaymentsOutlined />}
                        />
                    </Grid>
                </Grid>

                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <GraficoRosca
                            titulo="Itens mais vendidos"
                            dados={itensAgrupados}
                            chave="quantidade"
                            cores={cores}
                            formatarTooltip={valor => `${valor} unidades`}
                            mensagemVazia="Nenhum item registrado"
                            icone={<DonutLargeOutlined />}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <GraficoRosca
                            titulo="Principais métodos de pagamento"
                            dados={metodosPagamentoAgrupados}
                            chave="valorTotal"
                            cores={cores}
                            formatarTooltip={valor => real(valor)}
                            mensagemVazia="Nenhum método de pagamento registrado"
                            icone={<DonutLargeOutlined />}
                        />
                    </Grid>
                </Grid>

                <Cartao>
                    <CabecalhoCartao>
                        <TituloCartao>Contas</TituloCartao>
                        {temContas && (
                            <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                                {caixa?.contas.length} no intervalo
                            </Typography>
                        )}
                    </CabecalhoCartao>

                    {temContas ? (
                        <Box sx={{ overflowX: "auto" }}>
                            <Table
                                size="small"
                                sx={{
                                    minWidth: 720,
                                    "& tbody td": {
                                        fontSize: "0.8125rem",
                                        padding: theme => theme.spacing(1.25, 2),
                                        borderBottom: theme => `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                                    },
                                    "& tbody tr:hover": {
                                        backgroundColor: theme => alpha(theme.palette.text.primary, 0.03),
                                    },
                                    "& tbody tr:last-of-type td": { borderBottom: "none" },
                                }}
                            >
                                <TableHead>
                                    <TableRow>
                                        <CelulaCabecalho>Descrição</CelulaCabecalho>
                                        <CelulaCabecalho>Data/Hora</CelulaCabecalho>
                                        <CelulaCabecalho>Valor total</CelulaCabecalho>
                                        <CelulaCabecalho>Tipo</CelulaCabecalho>
                                        <CelulaCabecalho>Pagamentos</CelulaCabecalho>
                                        <CelulaCabecalho>Itens</CelulaCabecalho>
                                        <CelulaCabecalho>Visualizar</CelulaCabecalho>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {caixa?.contas.map((conta, index) => (
                                        <CaixaTableRow key={`conta-${conta.uuid || index}`} conta={conta} />
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    ) : (
                        <EstadoVazio>
                            <Box component="span" sx={{ display: "flex" }}><ReceiptLongOutlined /></Box>
                            <Typography sx={{ fontSize: "0.8125rem" }}>
                                Nenhuma conta registrada no intervalo
                            </Typography>
                        </EstadoVazio>
                    )}
                </Cartao>
            </PaginaCaixa>

            {conferenciaAberta && caixa && (
                <ConferenciaCaixa
                    intervalo={caixa}
                    onClose={() => setConferenciaAberta(false)}
                />
            )}
        </>
    );
}
